import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  AI_CONSENT_TEXT,
  AI_CONSENT_VERSION,
  PURPOSES,
  isConsentAvailable,
} from '../../util/aiConsent';
import css from './AiDescriptionField.module.css';

/**
 * AiDescriptionField
 *
 * Two ways to fill the description field, and the seller picks which:
 *
 *   - "Generate with AI" sends allowlisted vehicle details to OpenAI. It is
 *     locked behind an unticked consent box and cannot be pressed until the
 *     seller ticks it.
 *   - "Use standard template" builds the text on our own server from
 *     everything the seller entered, including their notes, and sends nothing
 *     to OpenAI.
 *
 * Typing a description by hand is always available and always has been — the
 * field below this block is a plain textarea.
 *
 * The checkbox is a real gate, not a disclosure. Declining is a supported path
 * that produces a complete description, so a seller is never pushed into
 * consenting to finish their listing.
 *
 * The browser is not the enforcement point. The server re-checks consent and
 * applies its own field allowlist, so nothing here is load-bearing for privacy
 * — it is load-bearing for the seller understanding what they agreed to.
 *
 * Props:
 *   year, make, model   — required for a request; without them the control
 *                           explains what is missing instead of failing
 *   trim, color, odometer, drivetrain, location, condition, features,
 *   historyNotes, sellerNotes — context for the template. Free-text fields are
 *                           used by the template only; the server strips them
 *                           before anything reaches OpenAI.
 *   listingId, userId   — recorded in the consent audit log, when known
 *   hasExistingText     — true when the description field already has content,
 *                           so the button offers to replace rather than fill
 *   onGenerated         — (descriptionText) => void
 */
export default function AiDescriptionField({
  id = 'aiDescriptionConsent',
  year,
  make,
  model,
  trim,
  color,
  odometer,
  drivetrain,
  location,
  condition,
  features,
  historyNotes,
  sellerNotes,
  listingId,
  userId,
  hasExistingText,
  onGenerated,
}) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [source, setSource] = useState(null);
  // Set only when the server says the AI allowance was used up; never provider detail.
  const [limitNotice, setLimitNotice] = useState(null);
  // Unticked on every mount. Consent is per session and per version, never
  // remembered or pre-filled.
  const [consented, setConsented] = useState(false);

  // Read from the store rather than threaded through five wizard components.
  // It matters because a description is often generated before the draft
  // exists, and without this the audit record for that consent would identify
  // neither the listing nor the person who gave it.
  const currentUser = useSelector(state => state?.user?.currentUser);
  const resolvedUserId = userId || currentUser?.id?.uuid;

  const intl = useIntl();
  const consentAvailable = isConsentAvailable(PURPOSES.VEHICLE_DESCRIPTION, intl.locale);

  const canGenerate = !!year && !!make && !!model;

  /**
   * @param {Boolean} withConsent true only from the AI button, and only while
   *   the box is ticked. The template path omits the consent parameters
   *   entirely, so the server takes its no-OpenAI branch.
   */
  const handleGenerate = async withConsent => {
    if (!canGenerate) return;
    if (withConsent && !consented) return;
    setStatus('loading');

    const params = new URLSearchParams({
      year: String(year),
      make: String(make),
      model: String(model),
    });
    const addMaybe = (key, value) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(key, String(value));
      }
    };
    addMaybe('trim', trim);
    addMaybe('color', color);
    addMaybe('odometer', odometer);
    addMaybe('drivetrain', drivetrain);
    addMaybe('location', location);
    addMaybe('condition', condition);
    addMaybe('features', features);
    addMaybe('historyNotes', historyNotes);
    addMaybe('sellerNotes', sellerNotes);

    if (withConsent) {
      params.set('aiConsent', 'true');
      params.set('consentVersion', AI_CONSENT_VERSION);
      params.set('consentLocale', intl.locale);
      // Identify the consent in the audit log. Omitted rather than faked when
      // the listing has not been created yet.
      addMaybe('listingId', listingId);
      addMaybe('userId', resolvedUserId);
    }

    try {
      const res = await window.fetch(`/api/vehicle-description?${params.toString()}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success || !data.description) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      onGenerated?.(data.description);
      setSource(data.source || null);
      setLimitNotice(
        data.aiNotice ? { message: data.aiNotice, resetAt: data.aiResetAt || null } : null
      );
      setStatus('done');
    } catch (err) {
      // Never block the listing flow on this — the seller can always write
      // their own description and carry on.
      console.error('[AiDescriptionField] description unavailable:', err.message);
      setStatus('error');
    }
  };

  const busy = status === 'loading';
  const generatedByAi = source === 'AI Vehicle Description';

  return (
    <div className={css.root}>
      <div className={css.headerRow}>
        <div>
          <p className={css.title}>Write it for me</p>
          <p className={css.subtitle}>
            Builds a description from the details you&apos;ve entered. You can edit every word
            before publishing.
          </p>
        </div>
      </div>

      {!canGenerate ? (
        <p className={css.hint} role="status">
          {intl.formatMessage({ id: 'AiDescriptionField.missingVehicleData' })}
        </p>
      ) : null}

      {consentAvailable && (
        <>
          {/* Sits directly above the button it unlocks, so the agreement and the
              action it authorises are read together. Kept separate from the
              marketplace terms on purpose: this is one specific disclosure to one
              named third party, not a blanket acceptance. */}
          <div className={css.consentBlock}>
            <input
              type="checkbox"
              id={id}
              className={css.consentCheckbox}
              checked={consented}
              onChange={e => setConsented(e.target.checked)}
            />
            <label htmlFor={id} className={css.consentLabel}>
              {AI_CONSENT_TEXT}
            </label>
          </div>

          <div className={css.actions}>
            <button
              type="button"
              className={css.generateBtn}
              onClick={() => handleGenerate(true)}
              disabled={!canGenerate || !consented || busy}
            >
              {busy ? 'Writing…' : hasExistingText ? 'Rewrite with AI' : 'Generate with AI'}
            </button>
            <button
              type="button"
              className={css.templateBtn}
              onClick={() => handleGenerate(false)}
              disabled={!canGenerate || busy}
            >
              {intl.formatMessage({ id: 'AiDescriptionField.templateButton' })}
            </button>
          </div>

          {!consented && canGenerate ? (
            <p className={css.hint} role="status">
              {intl.formatMessage({ id: 'AiDescriptionField.noConsentHint' })}
            </p>
          ) : null}
        </>
      )}

      {!consentAvailable && canGenerate ? (
        <div className={css.actions}>
          <button
            type="button"
            className={css.templateBtn}
            onClick={() => handleGenerate(false)}
            disabled={!canGenerate || busy}
          >
            {intl.formatMessage({ id: 'AiDescriptionField.templateButton' })}
          </button>
        </div>
      ) : null}

      {status === 'done' ? (
        <p className={css.done} role="status">
          {generatedByAi
            ? intl.formatMessage({ id: 'AiDescriptionField.aiDraftAdded' })
            : intl.formatMessage({ id: 'AiDescriptionField.templateAdded' })}{' '}
          {intl.formatMessage({ id: 'AiDescriptionField.reviewResponsibility' })}
        </p>
      ) : null}

      {status === 'done' && limitNotice ? (
        <p className={css.error} role="status">
          {limitNotice.message}
          {limitNotice.resetAt
            ? ` ${intl.formatMessage(
                { id: 'AiDescriptionField.allowanceResets' },
                {
                  time: intl.formatDate(limitNotice.resetAt, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }),
                }
              )}`
            : ''}
        </p>
      ) : null}

      {status === 'error' ? (
        <p className={css.error} role="status">
          {intl.formatMessage({ id: 'AiDescriptionField.draftError' })}
        </p>
      ) : null}
    </div>
  );
}
